import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ThemeToggle } from '../components/ThemeProvider';
import { useAuthStore, useLanguageStore } from '../store';
import { 
  BookOpen, Calculator, Globe, Camera, Brain, BarChart3,
  Sparkles, ArrowRight, CheckCircle2, GraduationCap,
  Users, Shield, Zap
} from 'lucide-react';

/**
 * LandingPage Component - Email Authentication
 * Simple redirect to login page for authentication
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { t } = useLanguageStore();

  // Redirect authenticated users to dashboard — reads from store only,
  // no extra checkAuth() call (AppRouter's init already handled that)
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    { icon: BookOpen, title: 'Deutsch & Englisch', description: 'KI-gestützte Aufsätze, Analysen und Textfeedback.', color: 'text-primary' },
    { icon: Calculator, title: 'Mathematik', description: 'Schritt-für-Schritt Lösungen für alle Themen.', color: 'text-accent' },
    { icon: Globe, title: 'Nebenfächer', description: 'Bio, Geo, Geschichte, Physik – alles abgedeckt.', color: 'text-success' },
    { icon: Camera, title: 'Foto-Upload', description: 'Fotografiere Aufgaben – KI löst sie.', color: 'text-warning' },
    { icon: Brain, title: 'Spaced Repetition', description: 'Karteikarten mit intelligentem Wiederholsystem.', color: 'text-primary' },
    { icon: BarChart3, title: 'Lernstatistiken', description: 'Detaillierte Fortschrittsanalyse.', color: 'text-accent' }
  ];

  const trustItems = [
    'Für Schüler & Studierende',
    'Von Lehrern empfohlen',
    'KI-gestützt',
    'DSGVO-konform',
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Mentis" className="w-8 h-8 rounded-lg" />
            <span className="font-heading text-lg font-semibold">Mentis</span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <div className="hidden sm:block">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <Button onClick={handleLogin} data-testid="login-btn">
                  Anmelden
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                KI-gestützte Lernintelligenz
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Dein persönlicher KI-Tutor
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Mentis ist deine adaptive Lernintelligenz für Schule und Studium. 
                Aufsätze, Mathe, Prüfungsvorbereitung – alles an einem Ort.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Button size="lg" onClick={handleLogin} data-testid="hero-start-btn" className="rounded-full px-8">
                  Kostenlos starten
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })} className="rounded-full px-8">
                  Mehr erfahren
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  Keine Kreditkarte
                </span>
                <span className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-success" />
                  DSGVO-konform
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden md:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop" 
                  alt="Students learning"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-background/90 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Adaptive KI</p>
                    <p className="text-xs text-muted-foreground">Lernt mit dir</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Alles was du zum Lernen brauchst
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Von der Grundschule bis zur Uni – Mentis unterstützt dich in allen Fächern.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 ${feature.color}`}>
                      <feature.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Teacher Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-1.5 rounded-full text-sm mb-6">
                <GraduationCap className="w-4 h-4" />
                Für Lehrkräfte
              </div>
              
              <h2 className="font-heading text-3xl font-bold mb-6">
                Teacher-Dashboard für Ihre Klassen
              </h2>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Klassen verwalten und Schüler einladen',
                  'Fortschritt aller Schüler im Blick',
                  'Detaillierte Lernanalysen und Berichte',
                  'Hausaufgaben zuweisen und prüfen'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-success mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button onClick={handleLogin} variant="outline" className="rounded-full">
                Als Lehrer starten
                <Users className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="hidden md:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=400&fit=crop"
                  alt="Teacher with students"
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl font-bold mb-4">
              Bereit für bessere Noten?
            </h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">
              Starte jetzt kostenlos und entdecke, wie KI dein Lernen revolutioniert.
            </p>
            <Button 
              size="lg" 
              variant="secondary" 
              onClick={handleLogin}
              data-testid="cta-start-btn"
              className="rounded-full px-8"
            >
              Jetzt kostenlos starten
              <Zap className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Mentis" className="w-6 h-6 rounded" />
              <span className="text-sm text-muted-foreground">© 2024 Mentis. Alle Rechte vorbehalten.</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              {trustItems.map((item, index) => (
                <span key={index} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
